/** The legs, and everything you can do to one.
 *
 * Split out of `PositionsPanel` when that file outgrew its 400-line cap:
 * the panel is now the summary and the basket-wide controls, and this is
 * the table. They are read as one thing but edited separately.
 *
 * Three of the four cells here are EDITABLE, which is the whole point of
 * a replay you can argue with: the lots you would really have traded, the
 * price you would really have been filled at, and whether the leg is
 * still open at this minute.
 */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { SimPayoff } from "@/lib/simulatorApi";
import { CommitNumberInput } from "./CommitNumberInput";
import { MAX_LOTS, MIN_LOTS } from "./usePositions";
import { hhmm, money, pnlTone, signed } from "./positionsFormat";
import { MarkCell } from "./MarkCell";

/** One leg's P&L at the replayed minute, or null when it has no bar.
 *
 * Signed by direction: a SELL profits as the premium falls, which is the
 * sign error worth having in one place rather than inline in the JSX. */
function legPnl(leg: SimPayoff["legs"][number]): number | null {
  if (leg.ltp === null) return null;
  return (leg.direction === "BUY" ? 1 : -1) * (leg.ltp - leg.entry) * leg.quantity;
}

/** Lots, editable in place.
 *
 * A typed field AND steppers, because the two are used differently: the
 * steppers are for nudging a size you are already looking at, and the
 * field is for going straight to 10 without eight clicks.
 *
 * ⚠️ Bounded to the API's own 1..100. A leg outside that range fails the
 * whole basket's pricing call, not just its own row, so the wrong number
 * must be unreachable rather than merely rejected afterwards. An empty
 * field is left alone while it is being typed in and only clamped on
 * commit — clamping mid-keystroke makes "10" impossible to type, because
 * the leading "1" would immediately become a valid value and fight you.
 */
function LotsCell({
  lots,
  label,
  onLots,
}: Readonly<{ lots: number; label: string; onLots: (n: number) => void }>): ReactElement {
  return (
    <span className="flex items-center justify-end gap-0.5">
      <button
        type="button"
        className="btn btn-ghost px-1.5 py-0 text-xs leading-none"
        aria-label={`One lot fewer, ${label}`}
        disabled={lots <= MIN_LOTS}
        onClick={() => {
          onLots(lots - 1);
        }}
      >
        −
      </button>
      <CommitNumberInput
        label={`Lots, ${label}`}
        className="input h-6 w-12 px-1 py-0 text-center font-mono text-xs tabular-nums"
        min={MIN_LOTS}
        max={MAX_LOTS}
        value={lots}
        onCommit={onLots}
      />
      <button
        type="button"
        className="btn btn-ghost px-1.5 py-0 text-xs leading-none"
        aria-label={`One lot more, ${label}`}
        disabled={lots >= MAX_LOTS}
        onClick={() => {
          onLots(lots + 1);
        }}
      >
        +
      </button>
    </span>
  );
}

function SideBadge({
  direction,
}: Readonly<{ direction: SimPayoff["legs"][number]["direction"] }>): ReactElement {
  return (
    <span
      className={cn(
        "pill",
        direction === "BUY" ? "bg-success-bg text-success" : "bg-danger-bg text-danger",
      )}
    >
      {direction === "BUY" ? "B" : "S"}
    </span>
  );
}

/** A closed leg's P&L is fixed at what it closed at, whatever the chain
 * says now. */
function bookedPnl(leg: SimPayoff["legs"][number]): number | null {
  if (leg.exit == null) return null;
  return (leg.direction === "BUY" ? 1 : -1) * (leg.exit - leg.entry) * leg.quantity;
}

/** Close BOOKS the leg; Remove deletes it as though it never happened.
 * Two different things, so two controls — and Close disappears once there
 * is nothing left to book. Reopen undoes a booking (operator, 2026-11-04:
 * *"closed position can have option to open"*): the leg is open again, still
 * entered at the same minute and price, and marks live from here on. */
function RowActions({
  leg,
  closed,
  onClose,
  onReopen,
  onRemove,
}: Readonly<{
  leg: SimPayoff["legs"][number];
  closed: boolean;
  onClose: () => void;
  onReopen: () => void;
  onRemove: () => void;
}>): ReactElement {
  const name = `${String(leg.strike)} ${leg.side}`;
  return (
    <span className="inline-flex items-center gap-1">
      {!closed && (
        <button
          type="button"
          className="btn btn-ghost px-1.5 py-0.5 text-[11px]"
          aria-label={`Close ${name}`}
          title="Book this leg at the current minute's price"
          onClick={onClose}
        >
          Close
        </button>
      )}
      {leg.exit_at != null && (
        <button
          type="button"
          className="btn btn-ghost px-1.5 py-0.5 text-[11px]"
          aria-label={`Reopen ${name}`}
          title="Undo the close: the leg is open again and marks live"
          onClick={onReopen}
        >
          Reopen
        </button>
      )}
      <button
        type="button"
        className="btn btn-ghost px-2 py-0.5 text-xs"
        aria-label={`Remove ${name}`}
        onClick={onRemove}
      >
        ×
      </button>
    </span>
  );
}

/** The fill price, editable in place.
 *
 * The chain's premium at the click minute is the honest default, but a
 * real fill is rarely the last traded price — and asking "what would a
 * worse fill have done to this" is most of why anyone replays a session.
 *
 * The arrows move by 1 rupee (operator, 2026-11-04: *"stepper of entry should
 * be increment by 1 not decimal"*). A typed price can still carry decimals.
 * The value applies when you leave the field, so typing 320 reprices once.
 */
function EntryCell({
  entry,
  label,
  onEntry,
}: Readonly<{ entry: number; label: string; onEntry: (n: number) => void }>): ReactElement {
  return (
    <CommitNumberInput
      label={`Entry price, ${label}`}
      className="input h-6 w-20 px-1 py-0 text-right font-mono text-xs tabular-nums"
      min={0}
      step={1}
      value={entry}
      onCommit={onEntry}
    />
  );
}

/** A dormant leg has no result yet, a closed one has a fixed one, and an
 * open one marks. */
function pnlOf(leg: SimPayoff["legs"][number], dormant: boolean, closed: boolean): number | null {
  if (dormant) return null;
  return closed ? bookedPnl(leg) : legPnl(leg);
}

function StrikeCell({ leg }: Readonly<{ leg: SimPayoff["legs"][number] }>): ReactElement {
  return (
    <td className="px-2 py-1.5 text-right font-mono tabular-nums">
      {leg.strike} {leg.side}
    </td>
  );
}

/** The lots you can edit, and the quantity they come to. */
function SizeCells({
  leg,
  onLots,
}: Readonly<{ leg: SimPayoff["legs"][number]; onLots: (n: number) => void }>): ReactElement {
  return (
    <>
      <td className="px-2 py-1.5">
        <LotsCell lots={leg.lots} label={`${String(leg.strike)} ${leg.side}`} onLots={onLots} />
      </td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-ink-muted">
        {leg.quantity}
      </td>
    </>
  );
}

function PnlCell({
  pnl,
  basis,
}: Readonly<{ pnl: number | null; basis: SimPayoff["basis"] }>): ReactElement {
  return (
    <td className={cn("px-2 py-1.5 text-right font-mono tabular-nums", pnlTone(pnl))}>
      {signed(pnl)}
      {money(pnl, basis)}
    </td>
  );
}

/** What one row can ask for. Bundled so the row and the table do not each carry
 * a column of callbacks. */
interface RowHandlers {
  onRemove: () => void;
  onLots: (n: number) => void;
  onEntry: (n: number) => void;
  onClose: () => void;
  onReopen: () => void;
  onMark: (n: number) => void;
  onExit: (n: number) => void;
  onResetMark: () => void;
}

function LegRow({
  leg,
  at,
  basis,
  on,
}: Readonly<{
  leg: SimPayoff["legs"][number];
  at: string;
  basis: SimPayoff["basis"];
  on: RowHandlers;
}>): ReactElement {
  // `!= null` on purpose: the API sends JSON null for an open leg, and
  // `!== undefined` would call every one of them closed.
  //
  // ⭐ BOTH ENDS ARE TIME-RELATIVE. Stepping the clock back past a leg's
  // entry makes it DORMANT again, and back past its exit makes it open
  // again — the same reading the server prices by, so the table and the
  // numbers beside it cannot disagree.
  const dormant = leg.entry_at != null && hhmm(at) < hhmm(leg.entry_at);
  const closed = leg.exit_at != null && hhmm(at) >= hhmm(leg.exit_at);
  const pnl = pnlOf(leg, dormant, closed);
  return (
    // A booked row is dimmed and says so: its numbers no longer move, and
    // a row that looks live but never changes reads as a stuck feed.
    <tr className={cn((closed || dormant) && "text-ink-secondary", dormant && "opacity-60")}>
      <td className="px-2 py-1.5">
        <SideBadge direction={leg.direction} />
      </td>
      <SizeCells leg={leg} onLots={on.onLots} />
      <StrikeCell leg={leg} />
      <td className="px-2 py-1.5 text-right">
        <EntryCell
          entry={leg.entry}
          label={`${String(leg.strike)} ${leg.side}`}
          onEntry={on.onEntry}
        />
      </td>
      <MarkCell
        leg={leg}
        closed={closed}
        dormant={dormant}
        onMark={on.onMark}
        onExit={on.onExit}
        onResetMark={on.onResetMark}
      />
      <PnlCell pnl={pnl} basis={basis} />
      <td className="px-2 py-1.5 text-right">
        <RowActions
          leg={leg}
          closed={closed}
          onClose={on.onClose}
          onReopen={on.onReopen}
          onRemove={on.onRemove}
        />
      </td>
    </tr>
  );
}

function LegsHead(): ReactElement {
  return (
    <thead>
      <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-secondary">
        <th className="px-2 py-2 text-left font-medium">B/S</th>
        <th className="px-2 py-2 text-right font-medium">Lots</th>
        <th className="px-2 py-2 text-right font-medium">Qty</th>
        <th className="px-2 py-2 text-right font-medium">Strike</th>
        <th className="px-2 py-2 text-right font-medium">Entry</th>
        <th className="px-2 py-2 text-right font-medium">LTP</th>
        <th className="px-2 py-2 text-right font-medium">P&amp;L</th>
        <th className="px-2 py-2" />
      </tr>
    </thead>
  );
}

interface TableProps {
  payoff: SimPayoff;
  at: string;
  onRemove: (index: number) => void;
  onLots: (index: number, lots: number) => void;
  onEntry: (index: number, entry: number) => void;
  onClose: (index: number) => void;
  onReopen: (index: number) => void;
  onMark: (index: number, price: number) => void;
  onExit: (index: number, price: number) => void;
  onResetMark: (index: number) => void;
}

/** The index-based callbacks of the table, narrowed to one row. */
function rowHandlers(p: TableProps, i: number): RowHandlers {
  return {
    onRemove: () => {
      p.onRemove(i);
    },
    onLots: (n) => {
      p.onLots(i, n);
    },
    onEntry: (n) => {
      p.onEntry(i, n);
    },
    onClose: () => {
      p.onClose(i);
    },
    onReopen: () => {
      p.onReopen(i);
    },
    onMark: (n) => {
      p.onMark(i, n);
    },
    onExit: (n) => {
      p.onExit(i, n);
    },
    onResetMark: () => {
      p.onResetMark(i);
    },
  };
}

function LegsTable(props: Readonly<TableProps>): ReactElement {
  const { payoff, at } = props;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <LegsHead />
        <tbody className="divide-y divide-line">
          {payoff.legs.map((leg, i) => (
            <LegRow
              key={`${String(leg.strike)}-${leg.side}-${String(i)}`}
              leg={leg}
              at={at}
              basis={payoff.basis}
              on={rowHandlers(props, i)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { LegsTable };
